import { NOODLE_CREATOR_ID } from '@/configuration/client';
import { isGladeAdmin, isProduction } from '@/providers/RoleBasedPermissions';

/**
 * @deprecated: Please use PostHog for feature flags.
 */
export type FEATURE_FLAG_KEY =
  | 'can-edit-form-request-provider'
  | 'for-testing-feature-flags'
  | 'uscis'
  | 'workflow-template-modal'
  | 'create-workflow-template'
  | 'can-archive-workflow-template'
  | 'can-use-noodle-questionnaire'
  | 'is-noodle-creator'
  | 'pdf-fill-insights'
  | 'can-upgrade-glade-questionnaires'
  | 'bank-statements-organizer'
  | 'government-forms'
  | 'use-pacer-prod-environment'
  | 'can-debug-questionnaire-responses';

export type Profile = { id: string; email?: string | null } | null;
export const isNoodler = (profile: Profile): boolean =>
  Boolean(profile?.email && /@noodle\.shop/.test(profile.email)) ||
  Boolean(profile?.email && /@glade\.ai/.test(profile.email));

const NOODLE_LAW_CASEBUILDER_CREATOR_ID = 'clw9oyao3neis07lnaavr2nwf';

const enabledByEmail =
  (...emails: string[]) =>
  (profile: Profile): boolean =>
    Boolean(profile?.email && emails.includes(profile.email));

// TODO: Remove isFeatureFlagAccount if not used in the future. Currently unused and kept for potential feature flag account checks.
// const isFeatureFlagAccount = (profile: Profile): boolean => (
//   enabledByEmail('accounts+featureflag@glade.ai')(profile)
// );

const enabledByCreator =
  (...creatorIds: string[]) =>
  (creatorId?: string | null): boolean =>
    Boolean(creatorId && creatorIds.includes(creatorId));

const isNoodleCreator = (creatorId?: string | null): boolean => Boolean(creatorId && creatorId === NOODLE_CREATOR_ID);

type Data = {
  profile: Profile;
  creatorId?: string | null;
};

/**
 * @deprecated: Please use PostHog for feature flags.
 */
const checkFeatureFlag = (featureFlagKey: FEATURE_FLAG_KEY, { profile, creatorId }: Data): boolean => {
  if (featureFlagKey === 'for-testing-feature-flags') {
    return !isProduction() || isNoodler(profile);
  }

  if (featureFlagKey === 'workflow-template-modal') {
    return isNoodler(profile);
  }

  if (featureFlagKey === 'can-edit-form-request-provider') {
    return (
      !isProduction() ||
      enabledByEmail('accounts@glade.ai', 'pradeek@compleximmigration.com', 'sid.horse+test@gmail.com')(profile)
    );
  }

  if (featureFlagKey === 'uscis') {
    return !isProduction() || enabledByCreator('clh88zh9n07oa0bmyudap1ujz')(creatorId);
  }

  if (featureFlagKey === 'create-workflow-template') {
    return enabledByEmail('accounts@glade.ai')(profile);
  }

  if (featureFlagKey === 'can-archive-workflow-template') {
    return isNoodler(profile);
  }

  if (featureFlagKey === 'can-use-noodle-questionnaire') {
    return isNoodler(profile);
  }

  if (featureFlagKey === 'is-noodle-creator') {
    return isNoodleCreator(creatorId);
  }

  if (featureFlagKey === 'government-forms') {
    return !isProduction() || (isNoodler(profile) && enabledByCreator('noodle-law-casebuilder')(creatorId));
  }

  if (featureFlagKey === 'pdf-fill-insights') {
    return !isProduction() || isNoodler(profile);
  }

  if (featureFlagKey === 'can-upgrade-glade-questionnaires') {
    return isGladeAdmin(profile);
  }

  if (featureFlagKey === 'bank-statements-organizer') {
    return (
      !isProduction() ||
      enabledByCreator('noodle-law-casebuilder')(creatorId) ||
      enabledByCreator('van-horn-law-group-pa')(creatorId)
    );
  }

  if (featureFlagKey === 'use-pacer-prod-environment') {
    return isProduction() && !enabledByCreator(NOODLE_LAW_CASEBUILDER_CREATOR_ID)(creatorId);
  }

  if (featureFlagKey === 'can-debug-questionnaire-responses') {
    return !isProduction() || isNoodler(profile);
  }

  return false;
};

export default checkFeatureFlag;
